import React, { Suspense } from 'react';
import ReactDOM from 'react-dom';
import { I18nextProvider, initI18n } from '@wix/wix-i18n-config';
import { AppLoader } from './components/AppLoader';
import { PureExperimentsProvider as ExperimentsProvider } from '@wix/wix-experiments-react';
import Experiments from '@wix/wix-experiments';
import { AccountSettingsHttpApi } from './services/accountSettingsApi';
import { DealerApi } from './services/dealerApi';
import { Box, Loader } from '@wix/design-system';
import { detectPreset } from './utils/presetDetection';
import { conductExperiment } from './utils/conductExperiment';
import { createSuspensePromise } from './utils/suspensePromise';
import { PRESETS } from './utils/presets';
import { EXPERIMENTS, EXPERIMENT_SCOPES } from './utils/constants';
import { getCookie } from './utils/cookie';

// Initialize experiments with data from window.__EXPERIMENTS__ if available
const experiments = new Experiments({
  scope: 'users',
  ...(window.__EXPERIMENTS__ && { experiments: window.__EXPERIMENTS__ }),
});
sessionStorage.setItem('loginPageUrl', window.location.href);
const { preset, presetKey } = detectPreset(experiments);
const loaderColors = preset?.loaderColors?.fullPage;
const createAccountSettingsApi = () => new AccountSettingsHttpApi();
const createDealerApi = () => new DealerApi();
const i18n = window.__STATICS_URL__
  ? initI18n({
      locale: window.__LOCALE__,
      asyncMessagesLoader: (locale) =>
        import(`./assets/locales/messages_${locale}.json`),
      wait: true,
    })
  : initI18n({
      locale: window.__LOCALE__,
      messages: window.__MESSAGES__,
    });

// Conduct the signup-title A/B in parallel with i18n loading so the H1 is
// correct on first paint (no flicker) and any signup BI events
// (loginPageOnStart, reportSwitchToSignup) fire after Petri exposure.
const signupForFreeTitleExperimentValue =
  presetKey === PRESETS.WIX_2025
    ? conductExperiment({
        key: EXPERIMENTS.SIGNUP_FOR_FREE_TITLE_AB,
        scope: EXPERIMENT_SCOPES.SIGNUP_FOR_FREE_TITLE,
        entityId: getCookie('_wixCIDX'),
      })
    : false;

const signupForFreeTitleExperiment = createSuspensePromise(
  signupForFreeTitleExperimentValue,
  {
    defaultValue: false,
    timeoutMs: 3000,
    onError: (error) => {
      console.error('[SignupForFreeAB] conduction failed', error);
    },
  },
);

const FullPageLoader = (
  <Box
    height="50vh"
    verticalAlign="bottom"
    direction="vertical"
    align="center"
    style={
      loaderColors
        ? ({
            '--wds-loader-fill': loaderColors.fill,
            '--wds-loader-fill-loading': loaderColors.fillLoading,
          } as React.CSSProperties)
        : undefined
    }
  >
    <Loader />
  </Box>
);

const App = () => (
  <I18nextProvider i18n={i18n}>
    <ExperimentsProvider experiments={experiments}>
      <AppLoader
        createAccountSettingsApi={createAccountSettingsApi}
        createDealerApi={createDealerApi}
        i18n={i18n}
        signupForFreeABEnabled={signupForFreeTitleExperiment.read()}
      />
    </ExperimentsProvider>
  </I18nextProvider>
);

ReactDOM.render(
  <Suspense fallback={FullPageLoader}>
    <App />
  </Suspense>,
  document.getElementById('root'),
);
